import { FC, useEffect, useState } from "react";
import { FormContainerStateReturnType, useTrans } from "../../../hooks";
import { useToast, VStack } from "@chakra-ui/react";
import {
    User as Entity,
    UserApi as EntityApi,
    Partnership,
    PartnershipApi,
    User,
} from "../../../apis";
import { Controller, useForm } from "react-hook-form";
import { AppFormContainer } from "../../../containers";
import {
    AppFormControl,
    AppSelect,
    AppSelectOption,
} from "../../../components";
import { APPC } from "../../../config";

export const PartnershipCompanyReferenceTrainerForm: FC<{
    formContainerState: FormContainerStateReturnType;
    data: Partnership;
}> = ({ formContainerState, data }) => {
    // hook
    const { t } = useTrans();
    const toast = useToast();

    // state & const
    const [loading, setLoading] = useState(true);
    const [referenceTrainerOptions, setReferenceTrainerOptions] = useState<
        AppSelectOption[]
    >([]);
    const [selectedReferenceTrainer, setSelectedReferenceTrainer] =
        useState<AppSelectOption | null>(
            data?.internshipReferenceTrainer
                ? {
                      value: PartnershipApi.toResourceIRI(data.id),
                      label: (data?.internshipReferenceTrainer as User)
                          ?.fullName,
                  }
                : null
        );
    const USERTYPE = APPC.BE.User.USERTYPE;

    const { handleSubmit, reset, control } = useForm<Partnership>({
        mode: "onSubmit",
    });

    // handler
    const onSubmitHandler = async (formData: Partnership) => {
        setLoading(true);
        PartnershipApi.patchPartnershipChangeReferenceTrainer<
            Partnership,
            Partnership
        >(data.id, formData)
            .then(({ errorMessage, response }) => {
                if (errorMessage) {
                    toast({
                        title: errorMessage,
                        status: "error",
                    });
                } else if (response !== null) {
                    reset();
                    formContainerState.close();
                }
            })
            .finally(() => setLoading(false));
    };

    const loadUserOptions = () => {
        setLoading(true);
        const params = {
            [`order[name]`]: "asc",
            pagination: false,
            userType: USERTYPE.USERTYPE_INTERNSHIP_REFERENCE_TRAINER,
        };
        EntityApi.getCollection<Entity>(1, params)
            .then(({ response }) => {
                if (response !== null) {
                    setReferenceTrainerOptions(
                        response?.items?.map((e) => ({
                            value: EntityApi.toResourceIRI(e.id),
                            label: e.fullName,
                        })) || []
                    );
                }
            })
            .finally(() => setLoading(false));
    };

    // useEffect
    useEffect(() => {
        if (formContainerState.entityId) {
            loadUserOptions();
        }
    }, [formContainerState.entityId]);

    return (
        <AppFormContainer
            title={t(
                "padm.PartnershipCompanyReferenceTrainerForm:text.pageTitle"
            )}
            state={formContainerState}
            type={"modal"}
            size={"lg"}
            isFooter={true}
            submitLabel={t(
                "padm.PartnershipCompanyReferenceTrainerForm:modal.submit.label"
            )}
        >
            <form
                id="app-form"
                onSubmit={(e) => {
                    e.preventDefault();
                    handleSubmit(onSubmitHandler)();
                }}
            >
                <VStack gap={5} alignItems={"stretch"} minH={"400px"}>
                    <AppFormControl
                        label={t(
                            "padm.PartnershipCompanyReferenceTrainerForm:internshipReferenceTrainer.label"
                        )}
                    >
                        <Controller
                            control={control}
                            name="internshipReferenceTrainer"
                            render={({ field }) => (
                                <AppSelect<AppSelectOption>
                                    name={field.name}
                                    isClearable
                                    isLoading={loading}
                                    options={referenceTrainerOptions}
                                    value={selectedReferenceTrainer || null}
                                    placeholder={t(
                                        "padm.PartnershipCompanyReferenceTrainerForm:internshipReferenceTrainer.placeholder"
                                    )}
                                    onChange={(e) => {
                                        field.onChange(e?.value || null);
                                        setSelectedReferenceTrainer(
                                            e as AppSelectOption
                                        );
                                    }}
                                />
                            )}
                        />
                    </AppFormControl>
                </VStack>
            </form>
        </AppFormContainer>
    );
};
